<template lang="pug">
#permitted-function-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 家族の使用機能の変更
    .box-body.register-body
      .message.mb-2
        span 変更したい機能の「使用する／使用しない」を変更して更新してください。
        br
        span 契約プランで使用不可の機能は表示されません。非表示機能を使用する場合は、先に契約プランを変更してください。
      .function-list.d-flex.flex-column.align-items-center
        .content.d-flex.flex-row.align-items-center(v-for="item in permittedFunctionItems")
          .icon.d-flex.flex-column.align-items-center
            img(:src="item.iconUrl")
            span.used(v-if="item.permitted == true") 使用中
            span.unused(v-else) 未使用
          .function.flex-grow-1
            .title {{ item.title }}
            .text {{ item.text }}
          .buttons.d-flex.flex-row-reverse
            PermittedFunctionSwitch(v-model="item.updatedPermitted")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoPermittedFunctionList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="使用機能を更新しますか？" @ok="doUpdate")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoPermittedFunctionList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import FacilitiesService, { FacilityGetModel, FacilityPatchModel, PermittedFunctions } from "@/commons/services/Facilities";
import { PlanType } from "@/commons/services/Plans";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import PermittedFunctionSwitch from "@/pc/basics/PermittedFunctionSwitch.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    FormLabel,
    PermittedFunctionSwitch,
    PageLoading
  }
})
export default class PermittedFunctionList extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;

  /** 機能使用可否各行の情報 */
  permittedFunctionItems: Array<PermittedFunctionListItem> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 使用機能可否は施設の情報なので施設情報を取得する
    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;

    const permittedFunctionItems: Array<PermittedFunctionListItem> = this.createPermittedFunctionItems(facility);
    this.permittedFunctionItems = permittedFunctionItems;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設を取得する
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 機能の使用可否から目次の情報を作成する
   *
   * - 画像のURL等、保持していない情報が多いのでfor文等は使用できない
   */
  public createPermittedFunctionItems(facility: FacilityGetModel): Array<PermittedFunctionListItem> {
    let items: Array<PermittedFunctionListItem> = [
      {
        id: "opinion",
        permitted: facility.permittedFunctions.opinion,
        iconUrl: require("../../../assets/permitted-function-opinion-icon.png"),
        title: "家族からの連絡",
        text: "家族から施設に連絡・問い合わせが出来ます。施設では内容の確認と返信が出来ます。",
        linkUrl: "#opinion",
        updatedPermitted: facility.permittedFunctions.opinion
      },
      {
        id: "reservation",
        permitted: facility.permittedFunctions.reservation,
        iconUrl: require("../../../assets/permitted-function-reservation-icon.png"),
        title: "訪問面会予約",
        text: "家族が施設訪問の予約を出来ます。施設では面会スケジュール等の設定が出来ます。",
        linkUrl: "#reservation",
        updatedPermitted: facility.permittedFunctions.reservation
      },
      {
        id: "online-reservation",
        permitted: facility.permittedFunctions.onlineReservation,
        iconUrl: require("../../../assets/permitted-function-reservation-icon.png"),
        title: "オンライン面会",
        text: "家族と施設でビデオ通話できます。家族は面会予約、施設は面会スケジュール等の設定が出来ます。",
        linkUrl: "#online-reservation",
        updatedPermitted: facility.permittedFunctions.onlineReservation
      }
    ];

    // ライトプラン以上は使用機能変更を追加する
    const planType: PlanType = userStore.facility.planType;
    if (PlanType.LIGHT <= planType) {
      items = items.concat([
        {
          id: "outing",
          permitted: facility.permittedFunctions.outing,
          iconUrl: require("../../../assets/permitted-function-outing-icon.png"),
          title: "外出外泊届け",
          text: "家族が外出／外泊届けを申請できます。施設は曜日や時間などの設定ができます。",
          linkUrl: "#outing",
          updatedPermitted: facility.permittedFunctions.outing
        }
      ]);
    }

    // ベーシックプラン以上は使用機能変更を追加する
    if (PlanType.BASIC <= planType) {
      items = items.concat([
        {
          id: "event",
          permitted: facility.permittedFunctions.event,
          iconUrl: require("../../../assets/permitted-function-event-icon.png"),
          title: "イベント情報",
          text: "家族へイベント案内し、家族は参加申込みが出来ます。オンラインのライブ配信も出来ます。",
          linkUrl: "#event",
          updatedPermitted: facility.permittedFunctions.event
        },
        {
          id: "careimage",
          permitted: facility.permittedFunctions.careimage,
          iconUrl: require("../../../assets/permitted-function-careimage-icon.png"),
          title: "介護記録",
          text: "介護記録（画像もしくはPDF）を登録・公開すると、家族がLINEから見ることが出来ます。",
          linkUrl: "#careimage",
          updatedPermitted: facility.permittedFunctions.careimage
        },
        {
          id: "picture",
          permitted: facility.permittedFunctions.picture,
          iconUrl: require("../../../assets/permitted-function-picture-icon.png"),
          title: "写真印刷依頼（家族からの写真送付）",
          text: "家族が施設利用者（入居者）に見せたい写真をメッセージ付きで送ることが出来ます。",
          linkUrl: "#picture",
          updatedPermitted: facility.permittedFunctions.picture
        }
      ]);
    }

    return items;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### 使用機能を更新する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      if (this.facility) {
        const permittedFunctions: PermittedFunctions = {
          opinion: this.permittedFunctionItems[0].updatedPermitted,
          reservation: this.permittedFunctionItems[1].updatedPermitted,
          onlineReservation: this.permittedFunctionItems[2].updatedPermitted,
          outing: this.permittedFunctionItems[3] ? this.permittedFunctionItems[3].updatedPermitted : this.facility.permittedFunctions.outing,
          event: this.permittedFunctionItems[4] ? this.permittedFunctionItems[4].updatedPermitted : this.facility.permittedFunctions.event,
          careimage: this.permittedFunctionItems[5] ? this.permittedFunctionItems[5].updatedPermitted : this.facility.permittedFunctions.careimage,
          picture: this.permittedFunctionItems[6] ? this.permittedFunctionItems[6].updatedPermitted : this.facility.permittedFunctions.picture,
          // 施設関係者の機能はここでは更新しない
          otherUserOpinion: this.facility.permittedFunctions.otherUserOpinion,
          // 待機者の機能はここでは更新しない
          waiterOpinion: this.facility.permittedFunctions.waiterOpinion
        };
        this.facility.permittedFunctions = permittedFunctions;

        const param: FacilityPatchModel = this.createUpdateParam(this.facility);
        await this.facilitiesService.patch(userStore, param);

        // 機能使用可否が変更されるのでstoreを更新する
        if (this.facility) userStore.updateFacility({ facility: this.facility });

        // 更新成功ダイアログを表示する
        this.showUpdateSuccessModal = true;
      }
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設更新パラメータ作成
   */
  public createUpdateParam(facility: FacilityGetModel): FacilityPatchModel {
    const params: FacilityPatchModel = {
      name: facility.name ? this.$stringUtil.convertYen(facility.name) : "",
      tel: facility.tel ? facility.tel : "",
      url: facility.url ? this.$stringUtil.convertYen(facility.url) : "",
      permittedFunctions: facility.permittedFunctions,
      useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
      useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
      recruitmentRoom: facility.recruitmentRoom,
      updated: facility.updated
    };

    return params;
  }

  /**
   * ### 使用機能の確認・変更に遷移する
   */
  public gotoPermittedFunctionList(): void {
    this.$router.push(PcUrl.PermittedFunctionList);
    return;
  }
}

interface PermittedFunctionListItem {
  /** ページ内リンク用のID */
  id: string;
  /** 機能の使用可否 */
  permitted: boolean;
  /** アイコン画像URL */
  iconUrl: string;
  /** 機能名 */
  title: string;
  /** 機能説明文 */
  text: string;
  /** ページ内リンクURL */
  linkUrl: string;
  /** 変更後機能の使用可否 */
  updatedPermitted: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#permitted-function-edit {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .message {
    padding: 0 0 10px 10px;
  }

  // 使用機能の目次
  .function-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコン
      .icon {
        min-width: 60px;
        width: 60px;
        margin-right: 12px;
        // 使用中・未使用
        .used,
        .unused {
          font-weight: bold;
        }
        .used {
          color: $font-key-color;
        }
        .unused {
          color: $font-disable-font-color;
        }
      }
      // 機能名
      .function {
        margin-right: 12px;
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
